import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, timeAgo } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Loader2, Search, ShieldCheck, User as UserIcon } from "lucide-react";

const PLANS = ["free", "pro", "enterprise"];

export default function Admin() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const setPlan = async (id, plan) => {
    setBusy(id + plan);
    try {
      await api.post(`/admin/users/${id}/plan`, { plan });
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, plan } : u)));
      toast.success(`Granted ${plan.toUpperCase()} access`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy("");
    }
  };

  const toggleRole = async (id, current) => {
    const role = current === "admin" ? "user" : "admin";
    setBusy(id + "role");
    try {
      await api.post(`/admin/users/${id}/role`, { role });
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, role } : u)));
      toast.success(role === "admin" ? "Promoted to admin" : "Set to user");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy("");
    }
  };

  if (!users)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );

  const filtered = users.filter(
    (u) => u.name?.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <PageHeader
        tag="Admin"
        title="User Management"
        subtitle="Grant complimentary access to any plan, or promote users to admin."
        right={
          <div className="font-mono text-sm border border-primary/50 bg-primary/10 text-primary px-4 py-2">
            {users.length} users
          </div>
        }
      />

      <div className="relative mb-4 max-w-sm">
        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="admin-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or email..."
          className="pl-9 rounded-none bg-input border-border font-mono focus-visible:ring-primary focus-visible:ring-2"
        />
      </div>

      <div className="bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Scans</th>
              <th className="text-left px-4 py-3">Joined</th>
              <th className="text-left px-4 py-3">Grant plan</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`admin-user-${u.email}`} className="hover:bg-primary/[0.03] transition-colors">
                <td className="px-4 py-3">
                  <div className="font-mono">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                  <span className={`inline-block mt-1 px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${
                    u.plan === "free" ? "border-border text-muted-foreground" : "border-primary/50 text-primary bg-primary/10"
                  }`}>
                    {u.plan}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`role-toggle-${u.email}`}
                    onClick={() => toggleRole(u.id, u.role)}
                    disabled={busy === u.id + "role"}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                      u.role === "admin" ? "border-primary/50 text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                  >
                    {u.role === "admin" ? <ShieldCheck size={12} strokeWidth={1.5} /> : <UserIcon size={12} strokeWidth={1.5} />}
                    {u.role}
                  </button>
                </td>
                <td className="px-4 py-3 font-mono text-muted-foreground">{u.scans}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{timeAgo(u.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {PLANS.map((p) => (
                      <button
                        key={p}
                        data-testid={`grant-${p}-${u.email}`}
                        onClick={() => setPlan(u.id, p)}
                        disabled={busy === u.id + p || u.plan === p}
                        className={`px-2.5 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-40 ${
                          u.plan === p ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50 hover:text-primary"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-10 text-center text-muted-foreground font-mono text-sm">No users found.</div>
        )}
      </div>
    </div>
  );
}
